import { useState, useEffect } from 'react';
import { getIsStringDragAndDropFormat } from '../utilities/drag-and-drop.js';

/**
 * Drag hook for making an HTML element draggable.
 *
 * NOTE: For iOS Safari, dragging is enabled via long press.
 *
 * @example
 * const ref = useRef<HTMLDivElement | null>(null);
 * const { dragging } = useDrag({ data, format: 'custom', ref });
 * <div ref={ref}>Drag</div>
 *
 * @param options UseDragOptions<T>
 * @returns UseDragReturn
 */ const useDrag = (options)=>{
    const { data, disabled = false, dragImageRef, effectAllowed = 'uninitialized', format, ref } = options;
    const dragImage = dragImageRef?.current;
    const htmlElement = ref?.current;
    const [dragging, setDragging] = useState(false);
    const [mounted, setMounted] = useState(false);
    useEffect(()=>{
        setMounted(true);
    }, []);
    useEffect(()=>{
        if (disabled || !mounted || !htmlElement || !htmlElement.addEventListener) {
            if (disabled && mounted && htmlElement) {
                htmlElement.removeAttribute('draggable');
            }
            return;
        }
        htmlElement.setAttribute('draggable', 'true');
        const onDragStart = (e)=>{
            // allows child draggable to be dragged inside parent draggable
            e.stopPropagation();
            if (dragImage) {
                const parent = dragImage.getBoundingClientRect();
                const child = htmlElement.getBoundingClientRect();
                const x = child.left - parent.left + (e.clientX - child.left);
                const y = child.top - parent.top + (e.clientY - child.top);
                e.dataTransfer?.setDragImage(dragImage, x, y);
            }
            const isString = getIsStringDragAndDropFormat(format);
            e.dataTransfer?.setData(format, isString ? data : JSON.stringify(data));
            if (e.dataTransfer && effectAllowed !== 'uninitialized') {
                e.dataTransfer.effectAllowed = effectAllowed;
            }
            setDragging(true);
            htmlElement.ondragstart?.(e);
        };
        const onDragEnd = (e)=>{
            setDragging(false);
            htmlElement.ondragend?.(e);
        };
        htmlElement.addEventListener('dragstart', onDragStart);
        htmlElement.addEventListener('dragend', onDragEnd);
        return ()=>{
            htmlElement.removeEventListener('dragstart', onDragStart);
            htmlElement.removeEventListener('dragend', onDragEnd);
        };
    }, [
        data,
        disabled,
        dragImage,
        effectAllowed,
        format,
        htmlElement,
        mounted
    ]);
    return {
        dragging
    };
};

export { useDrag };
//# sourceMappingURL=use-drag.js.map
